import { useReducedMotion } from 'framer-motion';
import type { Transition, Variants } from 'framer-motion';

/* ====================================================================
 * Shared motion foundation — "washi-paper editorial"
 *
 * Calm body reveals + a few hero-only cinematic primitives. Every
 * animation is gated on prefers-reduced-motion via the hooks below;
 * the raw variants are only ever applied through `useRevealProps`
 * (or by callers that themselves check `useMotionSafe`).
 *
 * Easing: a soft editorial ease-out. Distances are small (ma/yohaku —
 * we move type a few px, never far).
 * ==================================================================== */

/** Soft ease-out used across all reveals (cubic-bezier control points). */
export const EASE_EDITORIAL: [number, number, number, number] = [0.22, 1, 0.36, 1];

/** Default reveal transition. */
export const revealTransition: Transition = {
  duration: 0.55,
  ease: EASE_EDITORIAL,
};

/* ---- Variants -------------------------------------------------------- */

export const fadeUp: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 },
};

export const fadeUpSm: Variants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0 },
};

export const fadeInLeft: Variants = {
  hidden: { opacity: 0, x: -24 },
  visible: { opacity: 1, x: 0 },
};

export const fadeInRight: Variants = {
  hidden: { opacity: 0, x: 24 },
  visible: { opacity: 1, x: 0 },
};

export const scaleIn: Variants = {
  hidden: { opacity: 0, scale: 0.96 },
  visible: { opacity: 1, scale: 1 },
};

/** Parent wrapper that staggers its `variant` children on reveal. */
export const staggerContainer: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.04,
    },
  },
};

/** All named reveal variants, for typed lookup in <Reveal variant=…>. */
export const revealVariants = {
  fadeUp,
  fadeUpSm,
  fadeInLeft,
  fadeInRight,
  scaleIn,
  staggerContainer,
} as const;

export type RevealVariantName = keyof typeof revealVariants;

/* ---- Hooks ----------------------------------------------------------- */

/** `true` when motion is allowed (i.e. NOT prefers-reduced-motion). */
export function useMotionSafe(): boolean {
  return !useReducedMotion();
}

export interface RevealProps {
  initial: string | false;
  whileInView?: string;
  animate?: string;
  viewport?: { once: true; margin: string };
  transition?: Transition;
}

/**
 * Props for a scroll-reveal element. Spread onto a motion element that
 * declares `variants`. When prefers-reduced-motion is set, returns the
 * final state instantly (`initial: false`, no transition) so content is
 * fully visible with no transform.
 */
export function useRevealProps(delay = 0): RevealProps {
  const reduced = useReducedMotion();

  if (reduced) {
    // Render the "visible" state immediately, no animation.
    return { initial: false, animate: 'visible' };
  }

  return {
    initial: 'hidden',
    whileInView: 'visible',
    viewport: { once: true, margin: '-60px' },
    transition: { ...revealTransition, delay },
  };
}
